/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 * 
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 * 
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file sets up the logic for retrieving an individual patient.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Used to create logic based side effects for the redux store.
 */
import { createLogic } from 'redux-logic';

/**
 * Typings
 */
import { TypedUseSelectorHook, useSelector } from 'react-redux';

/**
 * Utility library for reducing redux boilerplate while remaining typed. 
 */
import { ImmerReducer, createActionCreators, createReducerFunction } from 'immer-reducer';

/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

 /*
  * Used to register the logics so that they are included in the store. 
  */
import reducerRegistry from '../ReducerRegistry';

/*
 * Used to get access to the API types and requests
 */
import * as Dtos from '../../api/dtos';

/**
 * Enumeration used to determine the state of a request.
 */
import { IRequestState, RequestState } from '.';

/**
 * Used to create a get request to the servicestack API.
 */
import { createGetRequest } from 'src/utils/createRequest';

/**
 * Typings
 */
import { ILogicSideEffect } from 'src/utils/logicTypes';

/*
* ---------------------------------------------------------------------------------
* Interfaces / Types
* ---------------------------------------------------------------------------------
*/

interface IIndividualGradeState {
    version: Dtos.Version | null;
    category: Dtos.Category | null;
    term: Dtos.Term | null;
    subterm: Dtos.Term | null;
    grade: Dtos.Grade | null;
    loadState: IRequestState;
}

interface IInitialGradeState extends Record<number, IIndividualGradeState> {
}

export interface IGradeState {
   grade: IInitialGradeState;
}

/*
 * ---------------------------------------------------------------------------------
 * Initial State
 * ---------------------------------------------------------------------------------
 */

const GradeInitialIndividualState: IIndividualGradeState = {
    version: null,
    category: null,
    term: null,
    subterm: null,
    grade: null,
    loadState: {
        state: RequestState.None
    }
};

const GradeInitialState: IInitialGradeState = {
};

/*
* ---------------------------------------------------------------------------------
* Selectors
* ---------------------------------------------------------------------------------
*/

export const useGradeSelector: TypedUseSelectorHook<IGradeState> = useSelector;

export const GradeSelectors = {
    selectGradeVersion: (state: IGradeState, gradeId: number) => state.grade[gradeId] ? state.grade[gradeId].version : GradeInitialIndividualState.version,
    selectGradeCategory: (state: IGradeState, gradeId: number) => state.grade[gradeId] ? state.grade[gradeId].category : GradeInitialIndividualState.category,
    selectGradeTerm: (state: IGradeState, gradeId: number) => state.grade[gradeId] ? state.grade[gradeId].term : GradeInitialIndividualState.term,
    selectGradeSubterm: (state: IGradeState, gradeId: number) => state.grade[gradeId] ? state.grade[gradeId].subterm : GradeInitialIndividualState.subterm,
    selectGrade: (state: IGradeState, gradeId: number) => state.grade[gradeId] ? state.grade[gradeId].grade : GradeInitialIndividualState.grade,
    selectGradeLoadState: (state: IGradeState, gradeId: number) => state.grade[gradeId] ? state.grade[gradeId].loadState : GradeInitialIndividualState.loadState
};

/*
* ---------------------------------------------------------------------------------
* Reducer
* ---------------------------------------------------------------------------------
*/

class GradeReducer extends ImmerReducer<IInitialGradeState> {
    public loadById(gradeId: number) {
        if (!this.draftState[gradeId]) {
            this.draftState[gradeId] = {...GradeInitialIndividualState};
        }

        this.draftState[gradeId].loadState = {
            state: RequestState.Pending
        };
    }

    public loadSuccess(version: Dtos.Version, category: Dtos.Category, term: Dtos.Term, subterm: Dtos.Term | null, grade: Dtos.Grade) {
        if (!this.draftState[grade.id]) {
            this.draftState[grade.id] = {...GradeInitialIndividualState};
        }

        this.draftState[grade.id].version = version;
        this.draftState[grade.id].category = category;
        this.draftState[grade.id].term = term;
        this.draftState[grade.id].subterm = subterm;

        this.draftState[grade.id].grade = grade;
        this.draftState[grade.id].loadState = {
            state: RequestState.Success
        };
    }

    public loadFailure(gradeId: number, responseStatus: Dtos.ResponseStatus) {
        if (!this.draftState[gradeId]) {
            this.draftState[gradeId] = {...GradeInitialIndividualState};
        }

        this.draftState[gradeId].version = GradeInitialIndividualState.version;
        this.draftState[gradeId].category = GradeInitialIndividualState.category;
        this.draftState[gradeId].term = GradeInitialIndividualState.term;
        this.draftState[gradeId].subterm = GradeInitialIndividualState.subterm;
        this.draftState[gradeId].grade = GradeInitialIndividualState.grade;
        this.draftState[gradeId].loadState = {
            state: RequestState.Failure,
            responseStatus
        };
    }

    public clear(gradeId: number) {
        delete this.draftState[gradeId];
    }

    public clearAll() {
        return {...GradeInitialState};
    }
}

export const GradeActions = createActionCreators(GradeReducer);
const reducer = createReducerFunction(GradeReducer, GradeInitialState);

/*
 * ---------------------------------------------------------------------------------
 * API
 * ---------------------------------------------------------------------------------
 */

export const GradeApi = {
    loadById: createGetRequest(
        Dtos.GetGradeById,
        (id: number) => ({
            id
        })
    )
}

/*
 * ---------------------------------------------------------------------------------
 * Logic
 * ---------------------------------------------------------------------------------
 */

const GradeLogic = {
    loadById: createLogic({
        type: GradeActions.loadById.type,
        latest: true,
        process: async ({ action }: ILogicSideEffect<IGradeState, typeof GradeActions.loadById>, dispatch, done) => {
            try {
                const response = await GradeApi.loadById(action.payload);
                dispatch(GradeActions.loadSuccess(
                    response.version,
                    response.category,
                    response.parentTerm ? response.parentTerm : response.term,
                    response.parentTerm ? response.term : null,
                    response.grade
                ));
            }
            catch (error) {
                dispatch(GradeActions.loadFailure(action.payload, error ? error.responseStatus : undefined));
            }

            done();
        }
    })
};

const logics = [
    GradeLogic.loadById
]

/*
 * ---------------------------------------------------------------------------------
 * Register
 * ---------------------------------------------------------------------------------
 */

reducerRegistry.register('grade', reducer, logics);


// WEBPACK FOOTER //
// ./src/store/reducers/grade.ts